import { useParams, Navigate, useNavigate } from "react-router-dom";
import { useState } from "react";

import { ModalDialog } from "@components/modal_dialog";
import { useServerData } from "@hooks";
import { useTeamMemberQuery } from "@gql";

import { PropertyListings } from "./PropertyListings/PropertyListings";
import { TeamMemberEdit } from "./TeamMemberEdit/TeamMemberEdit";
import { useTeamMemberDestroyMutation } from "./TeamMemberDestroyMutation";
import * as styles from "./TeamMember.styles";

const TeamMember = () => {
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const { slug } = useParams();
  const { signedIn } = useServerData();
  const navigate = useNavigate();

  const [destroyTeamMember, { loading: destroyTeamMemberLoading }] = useTeamMemberDestroyMutation();

  const { loading, data, error } = useTeamMemberQuery({
    variables: { slug: slug || "" },
  });

  if (error) {
    return <Navigate to="/" replace />;
  }

  if (loading || !data?.teamMember) return null;

  const { id, description, name, avatarLarge, position, phone, listingAgent } = data.teamMember;

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete: ${name}? This can't be undone.`)) {
      await destroyTeamMember({
        variables: { id },
        update: cache => {
          const teamMemberToEvict = cache.identify(data.teamMember);

          if (cache.evict({ id: teamMemberToEvict })) {
            cache.gc();
          }
        },
      });

      navigate("/");
    }
  };

  const renderAbout = () => {
    if (!description) return null;

    // TextEditor injects <p> tags with no content
    // This is a simple way to check if there's actual content
    const testContainer = document.createElement("div");
    testContainer.innerHTML = description;

    if (!testContainer.innerText) return null;

    return (
      <>
        <h2 css={styles.h2}>About Me</h2>
        <div css={styles.aboutContainer} dangerouslySetInnerHTML={{ __html: description }} />
      </>
    );
  };

  const renderEditAndDestroyActions = () => {
    if (!signedIn) return;

    return (
      <div css={styles.actionButtonsContainer}>
        <button css={styles.editButton} className="btn" onClick={() => setIsEditOpen(true)}>
          Edit
        </button>
        <button
          css={styles.editButton}
          className="btn btn-danger"
          onClick={handleDelete}
          disabled={destroyTeamMemberLoading}
        >
          Destroy
        </button>
        <ModalDialog display={isEditOpen} onClosed={() => setIsEditOpen(false)}>
          <TeamMemberEdit teamMemberSlug={slug} onSave={() => setIsEditOpen(false)} />
        </ModalDialog>
      </div>
    );
  };

  const renderListings = () => {
    if (!listingAgent?.id) return;

    return <PropertyListings listingAgentId={listingAgent.id} />;
  };

  const renderAvatar = () => {
    if (!avatarLarge) return;

    return <img css={styles.img} src={avatarLarge} alt={name} />;
  };

  return (
    <div css={styles.teamMember}>
      <div css={styles.teamMemberInfo}>
        {renderAvatar()}

        <div css={styles.personalInfo}>
          <h1 css={styles.name}>{name}</h1>
          <h2 css={styles.h2}>{position}</h2>
          <p css={styles.contactInfo}>{phone}</p>
          {renderAbout()}
          {renderEditAndDestroyActions()}
        </div>
      </div>
      {renderListings()}
    </div>
  );
};

export { TeamMember };
